import React from "react";
import SuiBox from "components/SuiBox";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import ErrorAlert from "components/ErrorAlert";
import CustomSelect from "components/CustomSelect";
import MonthPicker from "components/MonthPicker";
import { Card, CircularProgress, Icon } from "@mui/material";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import { getUsersReq, getUnapprovedAbsenceReportReq } from "services/api";
import {
  customSelectStyles,
  REPORT_BUTTON_SX,
  REPORT_TOOLBAR_SX,
} from "../../reports.constants";
import moment from "moment";

// Darker than the dashboard card colours so the text reads on the grey banner.
const SUMMARY_COLORS = { wfh: "#0b8fb3", leave: "#ea0606", none: "#2e7d32" };

const formatDate =(date) => moment(date, "YYYY-MM-DD").format("Do MMMM YYYY");

const pluralize = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

function UnapprovedAbsenceReport() {
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(moment().format("YYYY-MM"));
  const [report, setReport] = useState(null);
  // Month and engineer of the shown report; picking another changes nothing
  // until Generate Report.
  const [reportMonth, setReportMonth] = useState(null);
  const [reportUserName, setReportUserName] = useState(null);

  const {
    auth: { admin, team, channel },
  } = useSelector((state) => state);

  const getUsers = async () => {
    setIsLoading(true);
    try {
      const { data } = await getUsersReq({
        admin_id: admin.id,
        team_id: team.id,
        channel_id: channel.id,
      });
      setUsers(data);
    } catch (err) {
      setErrorMessage(err.response?.data.message);
    }
    setIsLoading(false);
  };

  const generateReport = async () => {
    if (!selectedMonth) return;
    setIsReportLoading(true);
    try {
      const { data } = await getUnapprovedAbsenceReportReq({
        admin_id: admin.id,
        team_id: team.id,
        channel_id: channel.id,
        month: selectedMonth,
        user_id: selectedUser?.value,
      });
      setReport(data);
      setReportMonth(selectedMonth);
      // Option labels are "Name (username)".
      setReportUserName(selectedUser ? selectedUser.label.replace(/ \([^)]*\)$/, "") : null);
    } catch (err) {
      setErrorMessage(err.response?.data.message);
    }
    setIsReportLoading(false);
  };

  useEffect(() => {
    getUsers();
    generateReport();
  }, []);

  const renderPeople = (people) =>
    people.length === 0 ? (
      <SuiTypography variant="caption" color="text">
        —
      </SuiTypography>
    ) : (
      <>
        <SuiTypography variant="caption" color="black" fontWeight="bold" display="block">
          {people.length}
        </SuiTypography>
        <SuiTypography variant="caption" color="text" display="block">
          {people.map((person, index) => (
            <React.Fragment key={person.user_slack_id}>
              {index > 0 && ", "}
              <SuiBox
                component="a"
                href={`/reports/general-report?user=${encodeURIComponent(
                  person.user_slack_id
                )}&month=${reportMonth}`}
                target="_blank"
                rel="noopener noreferrer"
                display="inline"
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {person.name}
              </SuiBox>
            </React.Fragment>
          ))}
        </SuiTypography>
      </>
    );

  const renderSummary = () => {
    const { unapprovedWfhDays, unapprovedLeaveDays, wfhEngineers, leaveEngineers } = report.summary;
    const monthLabel = moment(reportMonth, "YYYY-MM").format("MMMM YYYY");

    const isClean = unapprovedWfhDays === 0 && unapprovedLeaveDays === 0;
    // One engineer: "Ali had ...", without engineer counts.
    const isSingle = !!reportUserName;
    const accent = (color, text) => (
      <SuiBox component="span" sx={{ color, fontWeight: "bold" }}>
        {text}
      </SuiBox>
    );
    const countPart = (days, engineers, kind, color) =>
      days === 0 ? (
        accent(SUMMARY_COLORS.none, `no unapproved ${kind} days`)
      ) : (
        <>
          {accent(color, pluralize(days, `unapproved ${kind} day`))}
          {!isSingle && ` (${pluralize(engineers, "engineer")})`}
        </>
      );

    return (
      <SuiBox
        display="flex"
        alignItems="center"
        p={2}
        sx={{
          gap: 1.5,
          backgroundColor: "#f8f9fa",
          borderRadius: "0.75rem",
          borderLeft: `4px solid ${isClean ? SUMMARY_COLORS.none : SUMMARY_COLORS.leave}`,
        }}
      >
        <Icon sx={{ color: isClean ? SUMMARY_COLORS.none : SUMMARY_COLORS.leave }}>
          {isClean ? "check_circle" : "event_busy"}
        </Icon>
        <SuiTypography variant="button" fontWeight="regular" color="dark">
          {isClean ? (
            <>
              {isSingle ? accent("inherit", reportUserName) : "No"}
              {isSingle ? " had no" : ""} unapproved WFH or leaves in{" "}
              {accent("inherit", monthLabel)}.
            </>
          ) : (
            <>
              In {accent("inherit", monthLabel)},{" "}
              {isSingle ? (
                <>{accent("inherit", reportUserName)} had </>
              ) : (
                "there were "
              )}
              {countPart(unapprovedWfhDays, wfhEngineers, "WFH", SUMMARY_COLORS.wfh)}
              {unapprovedWfhDays === 0 || unapprovedLeaveDays === 0 ? ", but " : " and "}
              {countPart(unapprovedLeaveDays, leaveEngineers, "leave", SUMMARY_COLORS.leave)}
              .
            </>
          )}
        </SuiTypography>
      </SuiBox>
    );
  };

  return (
    <>
      {errorMessage && (
        <ErrorAlert color="error" message={errorMessage} clearError={() => setErrorMessage(null)} />
      )}
      <SuiBox py={3}>
        <SuiBox display="flex" alignItems="center" flexWrap="wrap" sx={REPORT_TOOLBAR_SX}>
          <SuiBox className="w-340" sx={{ height: "35px" }}>
            {isLoading ? (
              <CircularProgress color="dark" size={18} />
            ) : (
              <CustomSelect
                key={users.length}
                isSearchable={true}
                isClearable={true}
                customStyles={customSelectStyles}
                options={users}
                value={selectedUser}
                placeholder="All Engineers"
                onChange={(data) => setSelectedUser(data)}
              />
            )}
          </SuiBox>
          <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
          <SuiButton
            variant="gradient"
            color="secondary"
            size="small"
            onClick={generateReport}
            sx={REPORT_BUTTON_SX}
          >
            Generate Report
            <Icon sx={{ fontWeight: "bold" }}>arrow_forward</Icon>
          </SuiButton>
        </SuiBox>
      </SuiBox>

      <SuiBox pb={3}>
        {isReportLoading ? (
          <Card>
            <SuiBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              p={3}
            >
              <CircularProgress color="dark" size={30} />
            </SuiBox>
          </Card>
        ) : report ? (
          <Card>
            <SuiBox p={3}>
              {renderSummary()}

              {report.days.length > 0 && (
                <SuiBox mt={3} sx={{ overflowX: "auto" }}>
                  <SuiBox
                    display="flex"
                    backgroundColor="#f8f9fa"
                    borderRadius="0.5rem 0.5rem 0 0"
                    p={2}
                    sx={{ borderBottom: "2px solid #dee2e6", minWidth: 700 }}
                  >
                    {["Date", "Unapproved WFH", "Unapproved Leaves"].map((label) => (
                      <SuiBox key={label} flex="1" px={1}>
                        <SuiTypography variant="button" fontWeight="bold" color="dark">
                          {label}
                        </SuiTypography>
                      </SuiBox>
                    ))}
                  </SuiBox>

                  <SuiBox sx={{ minWidth: 700 }}>
                    {report.days.map((day, index) => (
                      <SuiBox
                        key={day.date}
                        display="flex"
                        p={2}
                        sx={{
                          borderBottom:
                            index < report.days.length - 1 ? "1px solid #dee2e6" : "none",
                          "&:hover": { backgroundColor: "#f8f9fa" },
                        }}
                      >
                        <SuiBox flex="1" px={1}>
                          <SuiTypography variant="button" fontWeight="medium" color="black">
                            {formatDate(day.date)}
                          </SuiTypography>
                        </SuiBox>
                        <SuiBox flex="1" px={1}>
                          {renderPeople(day.wfh)}
                        </SuiBox>
                        <SuiBox flex="1" px={1}>
                          {renderPeople(day.leaves)}
                        </SuiBox>
                      </SuiBox>
                    ))}
                  </SuiBox>
                </SuiBox>
              )}
            </SuiBox>
          </Card>
        ) : null}
      </SuiBox>
    </>
  );
}

export default UnapprovedAbsenceReport;
